import React, { useState } from 'react';
import { TextField, Typography, Button, Pagination } from '@mui/material';
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import { handleSearch, handleKeyPress } from '../logic/search-logic';
import SearchResult from "./SearchResult";
import NoResultsMessage from "./NoResultsMessage";
import { useAuth } from "react-oidc-context";

function SearchComponent() {
    const [searchResults, setSearchResults] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [initialSearchInitiated, setInitialSearchInitiated] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalHits, setTotalHits] = useState(0);
    const [processingTime, setProcessingTime] = useState(0);

    const auth = useAuth();

    if (auth.isLoading) return <div>Loading authentication...</div>;
    if (auth.error) return <div>Auth Error: {auth.error.message}</div>;

    if (!auth.isAuthenticated) {
        return (
            <Container style={{ marginTop: 50, textAlign: 'center' }}>
                <Typography variant="h5">Please log in to search</Typography>
                <Button
                    variant="contained"
                    onClick={() => auth.signinRedirect()}
                    style={{ marginTop: 20, backgroundColor: '#001738' }}
                >
                    Log in with OneLogin
                </Button>
            </Container>
        );
    }

    const handleSearchButton = async () => {
        const token = auth.user?.access_token;
        try {
            const result = await handleSearch(
                searchQuery,
                1,
                setSearchResults,
                setInitialSearchInitiated,
                token,
                auth
            );

            const hits = result?.totalHits ?? 0;
            setTotalHits(hits);
            setTotalPages(hits ? Math.ceil(hits / 20) : 0);
            setProcessingTime(result?.processingTime ?? 0);
            setCurrentPage(1);
        } catch (error) {
            console.error('Error searching:', error.message);
        }
    };

    const handleSearchWithPagination = async (page) => {
        const token = auth.user?.access_token;
        try {
            const result = await handleSearch(
                searchQuery,
                page,
                setSearchResults,
                setInitialSearchInitiated,
                token
            );

            const hits = result?.totalHits ?? 0;
            setTotalHits(hits);
            setTotalPages(hits ? Math.ceil(hits / 20) : 0);
            setProcessingTime(result?.processingTime ?? 0);
            setCurrentPage(page);
        } catch (error) {
            console.error('Error searching:', error.message);
        }
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        handleSearchWithPagination(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // 1) Pagination soll optisch "volle Breite" haben -> UL über 100% Breite ausrichten
    const paginationSx = {
        width: '100%',
        '& .MuiPagination-ul': {
            width: '100%',
            justifyContent: 'center', // oder 'flex-start' wenn du linksbündig willst
        },
    };

    // 2) Cards (SearchResult) auf dieselbe Breite wie Header/Search ziehen (typisch: SearchResult Card hat maxWidth/Auto)
    //    -> nur im Results-Bereich überschreiben
    const resultsColumnSx = {
        width: '100%',
        // Card basiert auf Paper -> beide abdecken
        '& .MuiCard-root': { width: '100%', maxWidth: '100%' },
        '& .MuiPaper-root': { width: '100%', maxWidth: '100%' },
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 2, width: '100%' }}>
                {/* Search Row */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'nowrap',
                        width: '100%',
                    }}
                >
                    <TextField
                        label="Search"
                        variant="outlined"
                        fullWidth
                        InputProps={{ style: { borderRadius: 25 } }}
                        InputLabelProps={{ shrink: true }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, handleSearchButton)}
                        sx={{ flex: 1, minWidth: 0 }}
                    />

                    <Button
                        variant="contained"
                        onClick={handleSearchButton}
                        sx={{
                            backgroundColor: '#001738',
                            borderRadius: 25,
                            height: 56,
                            px: 3,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                        }}
                    >
                        SEARCH IN SUNRISE-TICKETS
                    </Button>
                </Box>

                <Box mt={1} ml={1}>
                    <Typography variant="caption" color="textSecondary">
                        Search uses <strong>query strings</strong> with the default operator <strong>"AND"</strong> between words.
                        Use quotation marks for exact phrases, "+" to require terms, and "-" to exclude terms.
                    </Typography>
                </Box>

                {/* Result Header (grauer Balken) */}
                <Box
                    sx={{
                        mt: 2,
                        backgroundColor: '#FAFAFA',
                        borderRadius: 1,
                        p: 2,
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <Typography sx={{ fontFamily: 'Inter', fontWeight: 500, fontSize: 12 }}>
                        {totalHits} Results found in {processingTime.toFixed(4)} Seconds
                    </Typography>
                </Box>

                {/* Results + Pagination */}
                <Box sx={{ mt: 2, width: '100%' }}>
                    {searchResults.length > 0 ? (
                        <>
                            {/* Pagination oben (volle Breite) */}
                            <Box sx={{ ...paginationSx }}>
                                <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
                            </Box>

                            {/* Results (Cards auf volle Breite ziehen -> Header ist dann nicht mehr breiter) */}
                            <Box sx={{ mt: 2, ...resultsColumnSx }}>
                                {searchResults.map((result, index) => (
                                    <SearchResult key={index} result={result} />
                                ))}
                            </Box>

                            {/* Pagination unten (volle Breite) */}
                            <Box sx={{ mt: 2, ...paginationSx }}>
                                <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
                            </Box>
                        </>
                    ) : (
                        <NoResultsMessage
                            searchQuery={searchQuery}
                            filteredResults={searchResults}
                            initialSearchInitiated={initialSearchInitiated}
                        />
                    )}
                </Box>
            </Box>
        </Container>
    );
}

export default SearchComponent;
